import { useMutation } from '@apollo/client';
import { noop } from 'lodash';
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import {
  UpdateAutopaySettingsForLoanDocument,
  UpdateRepayAutopaySettingsForLoanDocument,
} from '@willow/graphql-iso/src/portal';
import { NamedMemo } from '@willow/shared-web';
import { LoanId, RepayAccountId } from '@willow/types-iso';

import { UserContext } from '../components/context/AppContexts';

interface AutopayContext {
  showConfirmAutopayModal: boolean;
  setShowConfirmAutopayModal: (val: boolean) => void;
  showDisableAutopayModal: boolean;
  setShowDisableAutopayModal: (val: boolean) => void;
  updateDwollaAutopaySettings: (loanId: LoanId, bankUrl?: string) => Promise<any>;
  updateRepayAutopaySettings: (loanId: LoanId, accountId?: RepayAccountId, dayOfTheMonth?: number) => Promise<any>;
  updateDwollaAutopaySettingsLoading: boolean;
  updateRepayAutopaySettingsLoading: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AutopayContext = createContext<AutopayContext>({
  showConfirmAutopayModal: false,
  showDisableAutopayModal: false,
  setShowConfirmAutopayModal: noop,
  setShowDisableAutopayModal: noop,
  updateDwollaAutopaySettings: async () => {},
  updateRepayAutopaySettings: async () => {},
  updateDwollaAutopaySettingsLoading: false,
  updateRepayAutopaySettingsLoading: false,
});

export const AutopayContextProvider = NamedMemo<{ children: ReactNode }>('AutopayContextProvider', ({ children }) => {
  const { id: portalUserId } = useContext(UserContext);

  const [showConfirmAutopayModal, setShowConfirmAutopayModal] = useState(false);
  const [showDisableAutopayModal, setShowDisableAutopayModal] = useState(false);

  const [updateAutopaySettingsForLoan, { loading: updateDwollaAutopaySettingsLoading }] = useMutation(
    UpdateAutopaySettingsForLoanDocument,
  );

  const [updateRepayAutopaySettingsForLoan, { loading: updateRepayAutopaySettingsLoading }] = useMutation(
    UpdateRepayAutopaySettingsForLoanDocument,
  );

  const updateDwollaAutopaySettings = useCallback(
    async (loanId: LoanId, bankUrl?: string) => {
      const variables = {
        loanId,
        autopaySettings:
          bankUrl && portalUserId
            ? {
                portalUserId,
                bankUrl,
              }
            : undefined,
      };
      return updateAutopaySettingsForLoan({ variables });
    },
    [portalUserId, updateAutopaySettingsForLoan],
  );

  const updateRepayAutopaySettings = useCallback(
    async (loanId: LoanId, accountId?: RepayAccountId, dayOfTheMonth?: number) => {
      return updateRepayAutopaySettingsForLoan({
        variables: {
          loanId,
          autopaySettings:
            accountId && portalUserId ? { accountId, portalUserId, dayOfTheMonth: dayOfTheMonth ?? 1 } : undefined,
        },
      });
    },
    [portalUserId, updateRepayAutopaySettingsForLoan],
  );

  return (
    <AutopayContext.Provider
      value={{
        showConfirmAutopayModal,
        setShowConfirmAutopayModal,
        showDisableAutopayModal,
        setShowDisableAutopayModal,
        updateDwollaAutopaySettings,
        updateRepayAutopaySettings,
        updateDwollaAutopaySettingsLoading,
        updateRepayAutopaySettingsLoading,
      }}
    >
      {children}
    </AutopayContext.Provider>
  );
});
